import { isDeepEqual } from "@suse-o11y/is-deep-equal";
import { useCallback, useEffect, useRef } from "react";

import { useNavigateFrom } from "../../../components/react/link";

import { useRightPaneChain } from "./_right-pane-chain-context";
import {
	type RightPaneActiveTab,
	type RightPaneComponentItemType,
	type RightPaneEventItemType,
	type RightPaneEventsItemType,
	type RightPaneGroupItemType,
	type RightPaneItem,
	type RightPaneRelationItemType,
	type RightPaneSpanEventItemType,
	type RightPaneSpanItemType,
	RightPaneState,
} from "./RightPaneState";

const useUpdateRightPaneState = () => {
	const navigateFrom = useNavigateFrom();

	return useCallback(
		(transform: (current: RightPaneState) => RightPaneState): void => {
			navigateFrom("*", {
				search: ({ rightPane = RightPaneState.defaults, ...search }) => ({
					...search,
					rightPane: RightPaneState.normalize(transform(rightPane)),
				}),
			});
		},
		[navigateFrom],
	);
};

export const useToggleRightPane = (): VoidFunction => {
	const update = useUpdateRightPaneState();

	return useCallback(() => {
		update((current) => ({ ...current, hidden: !current.hidden }));
	}, [update]);
};

export const useCloseRightPane = (): VoidFunction => {
	const update = useUpdateRightPaneState();

	return useCallback(() => {
		update((current) => ({ ...current, hidden: true }));
	}, [update]);
};

export const useOpenRightPane = () => {
	const update = useUpdateRightPaneState();

	return useCallback(
		(tab?: RightPaneActiveTab) => {
			update((current) => ({ ...current, ...(tab ? { activeTab: tab } : {}), hidden: false }));
		},
		[update],
	);
};

export const useCloseRightPaneTab = (): VoidFunction => {
	const update = useUpdateRightPaneState();

	return useCallback(() => {
		update((current) => ({ ...current, activeTab: "pinned-items", chain: [] }));
	}, [update]);
};

interface OpenInRightPane {
	(type: RightPaneComponentItemType, nodeId: number | string): void;
	(type: RightPaneRelationItemType | RightPaneGroupItemType, nodeId: number): void;
	(type: RightPaneSpanItemType, traceId: string, rootSpanId: string, spanId: string): void;
	(type: RightPaneSpanEventItemType, traceId: string, rootSpanId: string, spanId: string, index: number): void;
	(type: RightPaneEventItemType, eventId: string, itemTime: number): void;
	(type: RightPaneEventsItemType, startTime: Date, endTime: Date, eventType: string | undefined): void;
}

export const useOpenInRightPane = (): OpenInRightPane => {
	const update = useUpdateRightPaneState();
	const chain = useRightPaneChain();
	const chainRef = useRef(chain);

	useEffect(() => {
		chainRef.current = chain;
	}, [chain]);

	return useCallback(
		(
			...args:
				| [RightPaneComponentItemType, number | string]
				| [RightPaneRelationItemType | RightPaneGroupItemType, number]
				| [RightPaneSpanItemType, string, string, string]
				| [RightPaneSpanEventItemType, string, string, string, number]
				| [RightPaneEventItemType, string, number]
				| [RightPaneEventsItemType, Date, Date, string | undefined]
		) => {
			const push = (item: RightPaneItem) => {
				update((current) => {
					const currentItem = RightPaneState.getCurrentItem(current);
					const nextState: RightPaneState = {
						// should always reveal the pane
						hidden: false,
						// should always switch to the selection tab
						activeTab: "selection",
						chain: chainRef.current,
					};

					if (!isDeepEqual(currentItem, item)) {
						return RightPaneState.pushItem(item, nextState);
					}

					// the item is currently active - don't change the chain
					return { ...nextState, chain: current.chain };
				});
			};

			if (args[0] === "event") {
				push({ type: "event", id: args[1], itemTime: args[2] });
			} else if (args[0] === "events") {
				push({ type: "events", startTime: args[1], endTime: args[2], eventType: args[3] });
			} else if (args[0] === "span") {
				push({ type: "span", traceId: args[1], primarySpanId: args[2], spanId: args[3] });
			} else if (args[0] === "span-event") {
				push({ type: "span-event", traceId: args[1], primarySpanId: args[2], spanId: args[3], index: args[4] });
			} else if (args[0] === "component") {
				push({ type: "component", id: args[1] });
			} else {
				push({ type: args[0], id: args[1] });
			}
		},
		[update],
	);
};

export const useGoBackInChain = (): VoidFunction => {
	const update = useUpdateRightPaneState();

	return useCallback(() => update(RightPaneState.popItem), [update]);
};
